import Image from "next/image";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import Defaqto from "@/components/molecules/quote-section/quote-cover/defaqto/defaqto";
import OnlineExclusiveBanner from "@/components/molecules/quote-section/quote-cover/online-exclusive-price-banner/online-exclusive-banner";
import { ANNUAL_MULTI_TRIP, PRODUCT } from "@/constants";
import { RootState } from "@/store/root-reducer";
import { PolicyPackagePrice } from "@/types/PolicyPackagePriceType";
import { resolveMagnoliaAssetUrl } from "@/utils/cms-image-resolver";
import defaqtoStyles from "@/utils/defaqto-brand-colors.utils";
import headerClassNames from "@/utils/get-brand-quote-cover-top-classes.utils";
import isShowExclusiveBanner from "@/utils/online-exclusive-price-banner-show.utils";

import styles from "./current-policy-cover-top.module.css";

type HeaderClassNamesType = {
  headerClassName: string;
  headerDetaisClassNames: {
    mainTextClassName: string;
    subTextClassName: string;
    renewalSubColor: string;
  };
  topSectionclassName: string;
};

type CurrentPolicyCoverTopProps = {
  content: {
    defaqtoRating: 1 | 2 | 3 | 4 | 5;
    coverName: string | undefined;
    coverType: string;
    coverBannerText: string;
    whichBestBuyBadge?: string;
  };
  currentPolicy: string;
};

export function CurrentPolicyCoverTop({
  content: {
    coverType,
    coverName,
    defaqtoRating,
    coverBannerText,
    whichBestBuyBadge,
  },
  currentPolicy,
}: Readonly<CurrentPolicyCoverTopProps>) {
  const headerClassNamesList: HeaderClassNamesType = headerClassNames[
    coverType as keyof typeof headerClassNames
  ] ?? {
    headerDetaisClassNames: "",
  };
  const { headerDetaisClassNames } = headerClassNamesList;
  const { attributes } = useSelector((state: RootState) => state.quote);

  const { current } = useWizard();
  const isDisplayWhichBestBuyBadge =
    current.brandSpecificParams?.isDisplayWhichBestBuyBadge;

  const renewalBadgeSrc = useMemo(
    () => (whichBestBuyBadge ? resolveMagnoliaAssetUrl(whichBestBuyBadge) : ""),
    [whichBestBuyBadge]
  );

  const shouldShowWhichBadge = useMemo(() => {
    const isEligibleCoverLevel =
      coverType === "COMPREHENSIVE" || coverType === "SIGNATURE";
    return Boolean(
      isDisplayWhichBestBuyBadge &&
        whichBestBuyBadge &&
        whichBestBuyBadge.trim().length > 0 &&
        isEligibleCoverLevel
    );
  }, [isDisplayWhichBestBuyBadge, whichBestBuyBadge, coverType]);

  const cover = useMemo(() => {
    return (
      attributes?.priceResult?.policyPackagePrice.find(
        (coverPackage: PolicyPackagePrice) =>
          coverPackage.policyType === ANNUAL_MULTI_TRIP &&
          coverPackage.policyPackage === currentPolicy
      ) ?? undefined
    );
  }, [attributes, currentPolicy]);

  const renderYearlyPrice = (
    total: number | undefined,
    loyaltyDiscountEligible: boolean | undefined,
    finalRetailENBPrice: number | undefined
  ) => {
    return (
      <div className="inline-table min-h-[16px] !h-[16px] ">
        <span
          className={`${headerDetaisClassNames.mainTextClassName} text-m font-[700] font-body-font leading-[113%]`}
        >
          {total ? `£${total.toFixed(2)}` : "N/A"}
        </span>
        &nbsp;
        {loyaltyDiscountEligible ? (
          <span
            className={`${headerDetaisClassNames.subTextClassName}  font-[400] text-2xs leading-[24px] line-through`}
          >
            {finalRetailENBPrice ? `£${finalRetailENBPrice.toFixed(2)}` : "N/A"}
          </span>
        ) : null}
      </div>
    );
  };

  const renderMonthlyPrice = (
    collectionAmount: number,
    totalCost: number | null
  ) => {
    return (
      <div className="inline-table min-h-[16px]!h-[16px]">
        <span
          className={`${headerDetaisClassNames.mainTextClassName} text-m font-[700] font-body-font leading-[113%]`}
        >
          {collectionAmount ? `£${collectionAmount.toFixed(2)}` : ""}
        </span>
        &nbsp;
        <span
          className={`${headerDetaisClassNames.renewalSubColor} text-xs font-sub-header-font leading-[21px]`}
        >
          a month&nbsp;
        </span>
        {totalCost ? (
          <span
            className={`${headerDetaisClassNames.subTextClassName} font-body-font text-2xs leading-[24px]`}
          >
            {`(£${totalCost.toFixed(2)} total)`}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <div className="relative">
      {cover?.loyaltyDiscountEligible && isShowExclusiveBanner.isShow && (
        <div
          className="absolute w-[98%] md:w-[100%] lg:w-[96%]"
          style={{ transform: `translateY(-50%)` }}
        >
          <div className="z-10 flex justify-center ">
            <OnlineExclusiveBanner
              onlineExclusiveBannerLabel={coverBannerText}
              background={PRODUCT === "avanti" ? "#4267D2" : "#006BD6"}
              className="font-semibold"
            />
          </div>
        </div>
      )}
      <div
        className={`pt-spacing-xl pr-spacing-m pb-spacing-m ${headerClassNamesList?.topSectionclassName} ${styles.backgroundColor} rounded-tl-[16px] rounded-tr-[16px] md:pt-spacing-l`}
      >
        {shouldShowWhichBadge && (
          <div className="hidden sm:block pointer-events-none sm:display- absolute right-[16px] md:right-[24px] top-[50px] md:top-[37.5px] z-[2]">
            <Image
              loader={() => renewalBadgeSrc}
              src={renewalBadgeSrc}
              width={101}
              height={80}
              alt="Which? Best Buy"
              objectFit="cover"
            />
          </div>
        )}
        <div
          className={`flex flex-col  gap-spacing-s items-start ${
            shouldShowWhichBadge ? "lg" : "md"
          }:items-center`}
        >
          <h1
            className={`${headerClassNamesList?.headerClassName} font-header-font text-xl leading-[115%] mx-spacing-m md:text-3xl md:leading-[109.677%]`}
          >
            {coverName}
          </h1>
          <div
            className={`px-spacing-xs py-spacing-xxs ${defaqtoStyles.background} ${styles.defaqtoBg} w-fit h-[27px] flex justify-end items-center rounded-tr-[4px] rounded-br-[4px]`}
          >
            <Defaqto stars={defaqtoRating} />
          </div>
          <div className="flex flex-col md:items-center md:flex-row pl-spacing-m mt-[-3px] md:mt-spacing-xs">
            {cover
              ? renderYearlyPrice(
                  cover?.total,
                  cover?.loyaltyDiscountEligible,
                  cover?.finalRetailENBPrice
                )
              : null}

            {cover &&
            !(
              cover?.collectionAmount === null ||
              cover?.collectionAmount === undefined
            ) ? (
              <>
                <div className="hidden md:block">
                  <p
                    className={`${headerDetaisClassNames.renewalSubColor} text font-font-semibold text-s leading-[115%]`}
                  >
                    &nbsp;or&nbsp;
                  </p>
                </div>
                {renderMonthlyPrice(cover.collectionAmount, cover.totalCost)}
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
